/**
 * Client API publique (événements publiés) — backend Mboapass.
 * Préfixe global Nest: /api/v1
 */

import { richTextToPlain } from "../utils/richText";

const DEFAULT_BASE = "http://localhost:3000/api/v1";

export function getPublicApiBaseUrl() {
  const base = process.env.REACT_APP_API_URL || DEFAULT_BASE;
  return base.replace(/\/$/, "");
}

function normalizeForCompare(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

/**
 * Associe la catégorie renvoyée par l'API à l'id d'onglet défini dans eventsData.json.
 * Si aucune correspondance : onglet « Tout » (1st-day).
 */
export function resolveCategoryTabId(apiCategory, categoryDefinitions) {
  if (!apiCategory || !String(apiCategory).trim()) {
    return "1st-day";
  }
  const raw = String(apiCategory).trim();
  const byId = categoryDefinitions.find((c) => c.id === raw);
  if (byId) return byId.id;
  const n = normalizeForCompare(raw);
  const byLabel = categoryDefinitions.find(
    (c) =>
      normalizeForCompare(c.name) === n ||
      normalizeForCompare(c.displayName) === n,
  );
  return byLabel ? byLabel.id : "1st-day";
}

export function formatEventDateTime(iso, timeZone = "Africa/Douala") {
  if (!iso) return { time: "", date: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { time: "", date: "" };
  const datePart = new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "long",
    timeZone,
  }).format(d);
  const timePart = new Intl.DateTimeFormat("fr-FR", {
    timeStyle: "short",
    timeZone,
  }).format(d);
  return { time: timePart, date: datePart };
}

/** Date et heure séparées pour la fiche événement publique (ex. 1 août 2026 / 15H00). */
export function formatPublicEventSchedule(iso, location = "", timeZone = "Africa/Douala") {
  const loc = String(location || "").trim();
  if (!iso) {
    return { date: "", time: "", location: loc };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { date: "", time: "", location: loc };
  }

  const day = new Intl.DateTimeFormat("fr-FR", { day: "numeric", timeZone }).format(d);
  const month = new Intl.DateTimeFormat("fr-FR", { month: "long", timeZone })
    .format(d)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
  const year = new Intl.DateTimeFormat("fr-FR", { year: "numeric", timeZone }).format(d);
  const dateLabel = `${day} ${month} ${year}`;

  const timeParts = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).formatToParts(d);
  const hour = timeParts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = timeParts.find((p) => p.type === "minute")?.value ?? "00";
  const timeLabel = `${hour}H${minute}`;

  return { date: dateLabel, time: timeLabel, location: loc };
}

const PLACEHOLDER_POSTER = "/images/events/309X166.jpg";

export function slugifyEventTitle(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isEventUuid(value) {
  return UUID_RE.test(String(value || "").trim());
}

/** Slug public d’un événement (API ou carte liste). */
export function getEventPublicSlug(eventOrCard) {
  const raw = eventOrCard?._raw ?? eventOrCard;
  if (!raw || typeof raw !== "object") {
    return String(eventOrCard?.slug || "").trim();
  }
  return (
    raw.public_slug ||
    eventOrCard?.slug ||
    slugifyEventTitle(raw.title) ||
    ""
  );
}

/** Chemin React Router vers la fiche événement (ex. /mon-evenement). */
export function getPublicEventPath(eventOrCard) {
  const slug = getEventPublicSlug(eventOrCard);
  if (slug) return `/${encodeURIComponent(slug)}`;
  const id = eventOrCard?.id || rawId(eventOrCard);
  if (id) return `/${encodeURIComponent(id)}`;
  return "/events";
}

function rawId(eventOrCard) {
  const raw = eventOrCard?._raw ?? eventOrCard;
  return raw?.id || eventOrCard?.id || "";
}

export function mapApiEventToCard(apiEvent, categoryDefinitions) {
  const { time, date } = formatEventDateTime(apiEvent.start_date);
  const tabId = resolveCategoryTabId(apiEvent.category, categoryDefinitions);
  let image = apiEvent.poster_url || PLACEHOLDER_POSTER;
  if (image && !/^https?:\/\//i.test(image) && !image.startsWith("/")) {
    image = `/${image}`;
  }
  return {
    id: apiEvent.id,
    slug: apiEvent.public_slug || slugifyEventTitle(apiEvent.title),
    title: apiEvent.title || "",
    description: richTextToPlain(apiEvent.description),
    category: tabId,
    time,
    date,
    location: apiEvent.location || "",
    image,
    ctaLabel: getPublicEventCtaLabel(apiEvent),
    _raw: apiEvent,
  };
}

/**
 * Libellé du bouton liste / accueil selon le mode de l’événement (billetterie vs enregistrement).
 * @param {object} eventOrCard — événement API ou carte avec `_raw`
 */
export function getPublicEventCtaLabel(eventOrCard) {
  const raw = eventOrCard?._raw ?? eventOrCard;
  if (!raw || typeof raw !== "object") return "Ticket";

  if (raw.registration_enabled === true) {
    return "Enregistrement";
  }
  return "Ticket";
}

async function parseJsonResponse(res) {
  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const msg = getApiErrorMessage(json, res.statusText);
    throw new Error(msg);
  }
  return json;
}

function normalizeProviderErrorMessage(message) {
  const raw = String(message || "").trim();
  const upper = raw.toUpperCase();

  if (upper.includes("ORANGE CARRIER UNSTABLE")) {
    return "Le réseau Orange Money est temporairement instable. Réessayez dans quelques instants ou utilisez MTN MoMo.";
  }
  if (upper.includes("MTN CARRIER UNSTABLE")) {
    return "Le réseau MTN MoMo est temporairement instable. Réessayez dans quelques instants ou utilisez Orange Money.";
  }
  if (upper.includes("CARRIER UNSTABLE")) {
    return "Le réseau Mobile Money est temporairement instable. Réessayez dans quelques instants.";
  }

  return raw;
}

function getApiErrorMessage(json, fallbackStatusText) {
  const fallback = fallbackStatusText || "Erreur API";
  if (!json || typeof json !== "object") return fallback;

  // Backend shape: { success:false, error:{ code, message }, ... }
  const nestedMessage = json?.error?.message;
  if (Array.isArray(nestedMessage) && nestedMessage.length) {
    return normalizeProviderErrorMessage(nestedMessage.join(" "));
  }
  if (typeof nestedMessage === "string" && nestedMessage.trim()) {
    return normalizeProviderErrorMessage(nestedMessage);
  }

  // Alternative shape: { message: "..." }
  if (typeof json?.message === "string" && json.message.trim()) {
    return normalizeProviderErrorMessage(json.message);
  }

  // Sometimes "error" can be a plain string.
  if (typeof json?.error === "string" && json.error.trim()) {
    return normalizeProviderErrorMessage(json.error);
  }

  return fallback;
}

/**
 * Liste paginée : { success, data: Event[], meta } après intercepteur Nest.
 */
export async function fetchPublicEvents({
  category = "",
  page = 1,
  limit = 200,
} = {}) {
  const base = getPublicApiBaseUrl();
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  params.set("page", String(page));
  params.set("limit", String(limit));
  const url = `${base}/events?${params.toString()}`;
  const json = await parseJsonResponse(await fetch(url));
  const list = Array.isArray(json?.data) ? json.data : [];
  const meta = json?.meta || null;
  return { events: list, meta };
}

/**
 * Détail public : l'intercepteur renvoie parfois { data: { data: event } } selon le contrôleur.
 */
export async function fetchPublicEventById(idOrSlug) {
  const base = getPublicApiBaseUrl();
  const url = `${base}/events/${encodeURIComponent(idOrSlug)}`;
  const json = await parseJsonResponse(await fetch(url));
  const entity = extractResponsePayload(json);
  if (
    entity &&
    typeof entity === "object" &&
    !Array.isArray(entity) &&
    entity.id
  ) {
    return entity;
  }
  return null;
}

/**
 * Aperçu public via token secret (événement non publié).
 */
export async function fetchPublicEventPreview(previewToken) {
  const base = getPublicApiBaseUrl();
  const url = `${base}/events/preview/${encodeURIComponent(previewToken)}`;
  const json = await parseJsonResponse(await fetch(url));
  const entity = extractResponsePayload(json);
  if (
    entity &&
    typeof entity === "object" &&
    !Array.isArray(entity) &&
    entity.id
  ) {
    return entity;
  }
  return null;
}

export function getPublicEventPreviewPath(slug, previewToken) {
  if (!previewToken) return "/";
  const safeSlug = slugifyEventTitle(slug) || slug || "event";
  return `/${encodeURIComponent(safeSlug)}/preview/${encodeURIComponent(previewToken)}`;
}

export function mapsEmbedUrlFromLocation(location) {
  if (!location || !String(location).trim()) {
    return "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4562.753041141002!2d-118.80123790098536!3d34.152323469614075!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80e82469c2162619%3A0xba03efb7998eef6d!2sCostco+Wholesale!5e0!3m2!1sbn!2sbd!4v1562518641290!5m2!1sbn!2sbd";
  }
  const q = encodeURIComponent(location.trim());
  return `https://maps.google.com/maps?q=${q}&z=14&output=embed`;
}

/**
 * Développe une couche `{ data: entity }` renvoyée par certains contrôleurs après l'intercepteur.
 */
export function extractResponsePayload(json) {
  const outer = json?.data;
  if (
    outer &&
    typeof outer === "object" &&
    !Array.isArray(outer) &&
    "data" in outer &&
    outer.meta === undefined
  ) {
    return outer.data;
  }
  return outer;
}

/**
 * Création de commande publique (sans auth).
 * @param {{ buyerEmail: string, buyerPhone?: string, buyerName?: string, items: { ticketTypeId: string, quantity: number }[], promoCode?: string, paymentMode?: 'FULL'|'INSTALLMENT' }} body
 */
export async function createPublicOrder(body) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/orders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

/**
 * Valide un code promo / billet offert pour un événement.
 * @param {string} eventId
 * @param {{ code: string, quantity?: number }} body
 */
export async function validatePublicPromoCode(eventId, body) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(
    `${base}/events/${encodeURIComponent(eventId)}/promo-codes/validate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

/**
 * @param {string} orderId
 * @param {{ paymentMethod: string, phone?: string }} body
 */
export async function initiatePublicPayment(orderId, body) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(
    `${base}/pay/orders/${encodeURIComponent(orderId)}/initiate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

export async function refreshPublicPaymentStatus(orderId) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(
    `${base}/pay/orders/${encodeURIComponent(orderId)}/refresh-status`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
  );
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

export async function fetchOrderStatus(orderId) {
  const base = getPublicApiBaseUrl();
  const url = `${base}/orders/${encodeURIComponent(orderId)}/status`;
  const json = await parseJsonResponse(await fetch(url));
  return extractResponsePayload(json);
}

// ─── Donation API ─────────────────────────────────────────────────────────────

/**
 * Récupère la config de collecte de dons publique d'un événement.
 * Renvoie null si la collecte n'est pas activée.
 */
export async function fetchDonationConfig(eventId) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/events/${encodeURIComponent(eventId)}/donation-config`);
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

/**
 * Crée un don (status PENDING).
 */
export async function createDonation(body) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/donations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

/**
 * Initie le paiement Mobile Money pour un don.
 */
export async function initiateDonationPayment(donationId, body) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/donations/${encodeURIComponent(donationId)}/pay`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

/**
 * Rafraîchit le statut de paiement d'un don.
 */
export async function refreshDonationPaymentStatus(donationId) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/donations/${encodeURIComponent(donationId)}/refresh-status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

/**
 * Récupère le statut d'un don.
 */
export async function fetchDonationStatus(donationId) {
  const base = getPublicApiBaseUrl();
  const url = `${base}/donations/${encodeURIComponent(donationId)}/status`;
  const json = await parseJsonResponse(await fetch(url));
  return extractResponsePayload(json);
}

// ─── Registration API ──────────────────────────────────────────────────────────

/**
 * Récupère le formulaire d'inscription public d'un événement.
 * Renvoie null si le formulaire n'est pas disponible ou ouvert.
 */
export async function fetchRegistrationForm(eventId) {
  const base = getPublicApiBaseUrl();
  try {
    const res = await fetch(`${base}/events/${encodeURIComponent(eventId)}/registration-form`);
    if (!res.ok) return null;
    const json = await parseJsonResponse(res);
    return extractResponsePayload(json);
  } catch {
    return null;
  }
}

/**
 * Soumet une inscription à un événement.
 */
export async function submitEventRegistration(eventId, body) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/events/${encodeURIComponent(eventId)}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}

/**
 * Envoie un message via le formulaire Contact du site public.
 */
export async function submitContactMessage(body) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await parseJsonResponse(res);
  return extractResponsePayload(json);
}
